/**
 * The endorphin kicks — confetti, floating "+XP", sounds and a buzz.
 *
 * Everything here is decoration and must never throw or block: a phone with
 * no AudioContext, no vibration or reduced motion still gets the job done.
 * Reduced motion turns the particles off (the words and colours still say
 * what happened).
 */

const reduced = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches

/* ── Sound ─────────────────────────────────────────────────────────────── */

let ctx: AudioContext | null = null

export function soundOn(): boolean {
  try { return localStorage.getItem('sound') !== 'off' } catch { return true }
}

export function setSound(on: boolean): void {
  try { localStorage.setItem('sound', on ? 'on' : 'off') } catch { /* private mode */ }
}

function audio(): AudioContext | null {
  if (!soundOn()) return null
  try {
    // Safari mutes Web Audio when the ringer switch is on silent unless the
    // page says it is media playback. Safari 17+; elsewhere it is a no-op.
    const session = (navigator as { audioSession?: { type: string } }).audioSession
    if (session && session.type !== 'playback') session.type = 'playback'
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

function tone(freq: number, at: number, dur: number, type: OscillatorType = 'sine', vol = 0.12): void {
  const a = audio()
  if (!a) return
  const o = a.createOscillator()
  const g = a.createGain()
  o.type = type
  o.frequency.value = freq
  g.gain.setValueAtTime(0.0001, a.currentTime + at)
  g.gain.exponentialRampToValueAtTime(vol, a.currentTime + at + 0.015)
  g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + at + dur)
  o.connect(g).connect(a.destination)
  o.start(a.currentTime + at)
  o.stop(a.currentTime + at + dur + 0.05)
}

export type Kind = 'tap' | 'sale' | 'badge' | 'level' | 'undo'

export function sound(kind: Kind): void {
  try {
    switch (kind) {
      case 'tap': tone(880, 0, 0.06, 'triangle', 0.08); break
      // A cash-register "cha-ching": two bright notes and a shimmer.
      case 'sale': [988, 1319].forEach((f, i) => tone(f, i * 0.09, 0.14, 'square', 0.07)); [1568, 2093].forEach((f, i) => tone(f, 0.2 + i * 0.05, 0.25, 'triangle', 0.06)); break
      case 'badge': [659, 784, 988, 1319].forEach((f, i) => tone(f, i * 0.08, 0.2, 'triangle')); break
      case 'level': [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, i * 0.09, 0.3, 'square', 0.06)); break
      case 'undo': tone(523, 0, 0.12, 'sine', 0.08); tone(392, 0.1, 0.18, 'sine', 0.08); break
    }
  } catch { /* decoration */ }
}

export function buzz(pattern: number | number[]): void {
  try { navigator.vibrate?.(pattern) } catch { /* not supported */ }
}

/* ── Confetti ──────────────────────────────────────────────────────────── */

const COLORS = ['#fde047', '#0b0f0c', '#fffdf4', '#ff4d6d', '#7dd3fc', '#fb923c', '#a855f7']

interface P { x: number; y: number; vx: number; vy: number; r: number; w: number; h: number; c: string; life: number; spin: number }

let canvas: HTMLCanvasElement | null = null
let parts: P[] = []
let running = false

function ensureCanvas(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!canvas) {
    canvas = document.createElement('canvas')
    canvas.className = 'fx-canvas'
    canvas.setAttribute('aria-hidden', 'true')
    document.body.appendChild(canvas)
  }
  const dpr = window.devicePixelRatio || 1
  if (canvas.width !== innerWidth * dpr) {
    canvas.width = innerWidth * dpr
    canvas.height = innerHeight * dpr
  }
  const c = canvas.getContext('2d')
  c?.setTransform(dpr, 0, 0, dpr, 0, 0)
  return c
}

function loop(): void {
  const c = ensureCanvas()
  if (!c) { running = false; return }
  c.clearRect(0, 0, innerWidth, innerHeight)
  parts = parts.filter((p) => p.life > 0 && p.y < innerHeight + 40)
  for (const p of parts) {
    p.vy += 0.25
    p.vx *= 0.99
    p.x += p.vx
    p.y += p.vy
    p.r += p.spin
    p.life -= 1
    c.save()
    c.globalAlpha = Math.min(1, p.life / 30)
    c.translate(p.x, p.y)
    c.rotate(p.r)
    c.fillStyle = p.c
    c.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
    c.restore()
  }
  if (parts.length) requestAnimationFrame(loop)
  else running = false
}

/** A burst of confetti from a point (defaults to screen centre). */
export function confetti(count = 60, x?: number, y?: number, spread = 1): void {
  if (reduced()) return
  try {
    const ox = x ?? innerWidth / 2
    const oy = y ?? innerHeight / 2.5
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2
      const v = (3 + Math.random() * 7) * spread
      parts.push({
        x: ox, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 5,
        r: Math.random() * 6, w: 6 + Math.random() * 6, h: 4 + Math.random() * 6,
        c: COLORS[i % COLORS.length] as string, life: 70 + Math.random() * 50, spin: (Math.random() - 0.5) * 0.4,
      })
    }
    if (!running) { running = true; requestAnimationFrame(loop) }
  } catch { /* decoration */ }
}

/** Confetti from both bottom corners — for the big moments. */
export function celebrate(): void {
  if (reduced()) return
  confetti(70, 0, innerHeight, 1.4)
  confetti(70, innerWidth, innerHeight, 1.4)
}

/** A floating label ("+45 XP", "$40") that rises and fades from an element or point. */
export function floatText(text: string, from?: { x: number; y: number }, tone = 'xp'): void {
  try {
    const el = document.createElement('div')
    el.className = `fx-float fx-${tone}`
    el.textContent = text
    el.setAttribute('aria-hidden', 'true')
    el.style.left = `${from?.x ?? innerWidth / 2}px`
    el.style.top = `${from?.y ?? innerHeight / 2}px`
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 1400)
  } catch { /* decoration */ }
}

/** The big one: a package just sold. */
export function saleKick(cents: number, xp: number): void {
  sound('sale')
  buzz([30, 40, 30])
  celebrate()
  floatText(`+$${Math.round(cents / 100)}`, { x: innerWidth / 2, y: innerHeight / 2.6 }, 'cash')
  if (xp > 0) setTimeout(() => floatText(`+${xp} XP`, { x: innerWidth / 2, y: innerHeight / 2.1 }), 250)
}

export function smallKick(e?: { clientX: number; clientY: number }, label?: string): void {
  sound('tap')
  buzz(15)
  const at = e ? { x: e.clientX, y: e.clientY } : undefined
  confetti(14, at?.x, at?.y, 0.6)
  if (label) floatText(label, at ? { x: at.x, y: at.y - 30 } : undefined, 'xp')
}
